import React from "react";
import { Check, RotateCcw, Sun, Moon, Monitor } from "lucide-react";
import { ACCENTS, type ColorMode, resolveAccent } from "@/theme/appearance";
import { useAppearance } from "@/theme/AppearanceProvider";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";

const MODES: { id: ColorMode; label: string; icon: React.ReactNode }[] = [
  { id: "light", label: "Light", icon: <Sun size={16} /> },
  { id: "dark", label: "Dark", icon: <Moon size={16} /> },
  { id: "system", label: "System", icon: <Monitor size={16} /> },
];

/** Two independent controls — color mode and accent. Light and dark each keep
 *  their OWN accent, so the picker sets whichever mode is on screen; switch mode
 *  above to set the other. Selections preview live on the app itself; the
 *  hosting modal owns Save/Discard. */
export function AppearancePanel() {
  const { colorMode, mode, accent, setColorMode, setAccent, reset } = useAppearance();
  const modeLabel = mode === "dark" ? "Dark" : "Light";
  return (
    <div data-ph-scope="appearance" className="px-7 py-6">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-ink-secondary max-w-md m-0">
          Personalize how Artie looks for you. Choose light, dark, or match your
          system — light and dark each keep their own accent color.
        </p>
        <Button variant="secondary" size="sm" data-ph="select_defaults" onClick={reset}>
          <RotateCcw size={14} /> Reset
        </Button>
      </div>

      <div className="mt-6 text-[11px] font-bold tracking-[0.09em] text-ink-muted">COLOR MODE</div>
      <div className="mt-3 inline-flex rounded-md border border-edge bg-surface p-1 gap-1">
        {MODES.map((m) => {
          const selected = m.id === colorMode;
          return (
            <button
              key={m.id}
              type="button"
              data-ph="select_mode"
              aria-pressed={selected}
              onClick={() => setColorMode(m.id)}
              className={cn(
                "flex items-center gap-2 rounded-sm px-4 py-2 text-[13.5px] font-semibold cursor-pointer transition-colors",
                selected ? "bg-accent text-white" : "text-ink-secondary hover:bg-hover",
              )}
            >
              {m.icon}
              {m.label}
            </button>
          );
        })}
      </div>

      <div className="mt-7 text-[11px] font-bold tracking-[0.09em] text-ink-muted">
        ACCENT COLOR · {modeLabel.toUpperCase()} MODE
      </div>
      <p className="mt-1.5 text-xs text-ink-muted m-0">
        The action &amp; highlight color for <b className="font-semibold">{modeLabel.toLowerCase()}</b> mode.
        Switch mode above to set the other one independently.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        {ACCENTS.map((a) => {
          const selected = accent === a.hex;
          // Preview the shade this swatch actually applies in the CURRENT mode
          // (deep on light, bright on dark) — so the dot matches the result.
          const shade = resolveAccent(a.hex, mode);
          return (
            <button
              key={a.hex}
              type="button"
              aria-label={`${a.name} accent`}
              data-ph="select_accent"
              aria-pressed={selected}
              onClick={() => setAccent(a.hex)}
              style={{ background: shade }}
              className={cn(
                "h-10 w-10 rounded-full cursor-pointer inline-flex items-center justify-center",
                "shadow-[0_0_0_1px_rgba(0,0,0,0.06)]",
                selected && "ring-[3px] ring-ink",
              )}
            >
              {selected && <Check size={16} className="text-white" strokeWidth={2.6} />}
            </button>
          );
        })}
      </div>
      <p className="mt-5 text-xs text-ink-muted leading-relaxed m-0">
        Both modes default to Emerald (the brand). Pick per mode to override;
        other accents are highlight-only.
      </p>
    </div>
  );
}
